<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div class="list-projects-wrapper content-wrapper">
  <b-loading :is-full-page="false" :active="loading" />
  <div class="box error" v-if="error">
    <h2> {{ $t('error') }} </h2>
    <p>{{ $t('unexpected-error-info-message') }}</p>
  </div>
  <div v-else-if="!loading" class="proj-page">
    <!-- toolbar: search (left) · Filters button + New project (right) -->
    <div class="proj-toolbar">
      <div class="tb-search">
        <span class="tb-ico"><i class="fas fa-search"></i></span>
        <input v-model="searchString" :placeholder="$t('search-placeholder')" type="search" />
      </div>

      <div class="tb-actions">
        <div class="filter-anchor" v-click-outside="closeFilters">
          <button
            class="trigger-btn"
            :class="{'is-open': filtersOpen, 'has-active': chips.length}"
            @click="toggleFilters"
          >
            <span class="icon"><i class="fas fa-filter"></i></span>
            <span>{{ $t('filters') }}</span>
            <span v-if="chips.length" class="count">{{ chips.length }}</span>
            <span class="fb-chev" :class="{up: filtersOpen}"><i class="fas fa-chevron-down"></i></span>
          </button>

          <div v-if="filtersOpen && draft" class="popover2">
            <div class="p2-head">
              <span><i class="fas fa-filter"></i> {{ $t('refine-projects') }}</span>
              <button class="link-btn" @click="resetDraft">{{ $t('reset-all') }}</button>
            </div>

            <div class="p2-row three">
              <div class="field">
                <span class="field-lbl"><i class="fas fa-hashtag"></i> {{ $t('ontology') }}</span>
                <ims-multiselect v-model="draft.ontologies" :options="availableOntologies"
                  label="name" track-by="id" :multiple="true" :allPlaceholder="$t('all-ontologies')" />
              </div>
              <div class="field">
                <span class="field-lbl"><i class="fas fa-user-shield"></i> {{ $t('my-role') }}</span>
                <ims-multiselect v-model="draft.roles" :options="availableRoles" multiple :searchable="false" :allPlaceholder="$t('all')" />
              </div>
              <div class="field">
                <span class="field-lbl"><i class="fas fa-tag"></i> {{ $t('tags') }}</span>
                <ims-multiselect v-model="draft.tags" :options="availableTags"
                  label="name" track-by="id" :multiple="true" :allPlaceholder="$t('all')" />
              </div>
            </div>

            <div class="p2-divider"></div>

            <div class="p2-ranges">
              <div class="field" v-for="r in rangeDefs" :key="r.key">
                <span class="field-lbl"><i class="fas" :class="r.icon"></i> {{ $t(r.label) }}</span>
                <div class="minmax">
                  <input class="mm-box" type="number" min="0" :max="r.max"
                    :value="draft[r.key][0]" @input="e => setDraftBound(r.key, 0, e.target.value)" />
                  <span class="mm-dash">{{ $t('range-to') }}</span>
                  <input class="mm-box" type="number" min="0" :max="r.max"
                    :value="draft[r.key][1]" @input="e => setDraftBound(r.key, 1, e.target.value)" />
                </div>
              </div>
            </div>

            <div class="p2-foot">
              <button class="button ghost-btn" @click="closeFilters">{{ $t('button-cancel') }}</button>
              <button class="button primary-btn" @click="applyFilters">{{ $t('apply-filters') }}</button>
            </div>
          </div>
        </div>

        <button v-if="!currentUser.guestByNow" class="button primary-btn new-proj" @click="creationModal = true">
          <span class="icon"><i class="fas fa-plus"></i></span>
          <span>{{ $t('new-project') }}</span>
        </button>
      </div>
    </div>

    <!-- active-filter chips -->
    <div v-if="chips.length" class="chips">
      <span v-for="c in chips" :key="c.key" class="chip">
        {{ c.label }}
        <button @click="clearChip(c.key)" :aria-label="$t('button-delete')"><i class="fas fa-times"></i></button>
      </span>
      <button class="chip-clear" @click="clearAll">{{ $t('clear-all') }}</button>
    </div>

    <!-- UI-7 C4: projects as a placeholder card grid; clicking a card opens the
         Project information pop-up (instead of an inline table expand). -->
    <div class="proj-area">
      <b-loading :is-full-page="false" :active="loadingPage" />
      <div v-if="projects.length" class="proj-grid columns is-multiline">
        <div
          class="column is-one-quarter-desktop is-one-third-tablet is-half-mobile"
          v-for="project in projects"
          :key="project.id"
        >
          <project-preview :project="project" @open="openDetail" />
        </div>
      </div>
      <div v-else-if="!loadingPage" class="content has-text-grey has-text-centered no-proj">
        <p>{{$t('no-project')}}</p>
      </div>

      <pager :total="total" :per-page="perPage" :current.sync="currentPage" />
    </div>

    <project-detail-modal
      :active.sync="detailModalActive"
      :project="detailProject"
      @update="updateProject"
      @delete="onModalDelete"
    />
  </div>

  <add-project-modal :active.sync="creationModal" :ontologies="ontologies" />
</div>
</template>

<script>
import ImsMultiselect from '@/components/form/ImsMultiselect';
import AddProjectModal from './AddProjectModal';
import ProjectPreview from './ProjectPreview';
import ProjectDetailModal from './ProjectDetailModal';
import Pager from '@/components/utils/Pager';

import {get, sync, syncBoundsFilter, syncMultiselectFilter} from '@/utils/store-helpers';

import {ProjectCollection, OntologyCollection, TagCollection} from '@/api';
export default {
  name: 'list-projects',
  components: {
    AddProjectModal,
    ProjectPreview,
    ProjectDetailModal,
    Pager,
    ImsMultiselect
  },
  data() {
    return {
      loading: true,
      error: false,

      projects: [],
      total: 0,
      loadingPage: false,
      detailModalActive: false,
      detailProject: null,

      ontologies: [],
      availableTags:[],

      contributorLabel: this.$t('contributor'),
      managerLabel: this.$t('manager'),

      creationModal: false,

      // compact filter popover: open state + a draft committed on "Apply filters"
      filtersOpen: false,
      draft: null,

      maxNbMembers: 10,
      maxNbImages: 10,
      maxNbUserAnnotations: 100,
      maxNbReviewedAnnotations: 100,

      revision: 0
    };
  },
  computed: {
    currentUser: get('currentUser/user'),

    searchString: sync('listProjects/searchString', {debounce: 500}),

    availableRoles() {
      return [this.contributorLabel, this.managerLabel];
    },
    availableOntologies() {
      return [{id: 'null', name: this.$t('no-ontology')}, ...this.ontologies];
    },

    querySearchTags() {
      return this.$route.query.tags;
    },
    selectedOntologies: syncMultiselectFilter('listProjects', 'selectedOntologies', 'availableOntologies'),
    selectedRoles: syncMultiselectFilter('listProjects', 'selectedRoles', 'availableRoles'),
    selectedTags: syncMultiselectFilter('listProjects', 'selectedTags', 'availableTags'),
    boundsMembers: syncBoundsFilter('listProjects', 'boundsMembers', 'maxNbMembers'),
    boundsImages: syncBoundsFilter('listProjects', 'boundsImages', 'maxNbImages'),
    boundsUserAnnotations: syncBoundsFilter('listProjects', 'boundsUserAnnotations', 'maxNbUserAnnotations'),
    boundsReviewedAnnotations: syncBoundsFilter('listProjects', 'boundsReviewedAnnotations', 'maxNbReviewedAnnotations'),

    nbActiveFilters() {
      return this.$store.getters['listProjects/nbActiveFilters'];
    },
    nbEmptyFilters() {
      return this.$store.getters['listProjects/nbEmptyFilters'];
    },

    selectedOntologiesIds() {
      return this.selectedOntologies.map(ontology => ontology.id);
    },

    boundsFilters() {
      return [
        {prop: 'numberOfImages', bounds: this.boundsImages, max: this.maxNbImages},
        {prop: 'membersCount', bounds: this.boundsMembers, max: this.maxNbMembers},
        {prop: 'numberOfAnnotations', bounds: this.boundsUserAnnotations, max: this.maxNbUserAnnotations},
        {prop: 'numberOfReviewedAnnotations', bounds: this.boundsReviewedAnnotations, max: this.maxNbReviewedAnnotations},
      ];
    },

    // the four numeric ranges rendered as Min/Max inputs in the popover.
    rangeDefs() {
      return [
        {key: 'members', boundsProp: 'boundsMembers', label: 'members', icon: 'fa-users', max: this.maxNbMembers},
        {key: 'images', boundsProp: 'boundsImages', label: 'images', icon: 'fa-images', max: this.maxNbImages},
        {key: 'userAnnotations', boundsProp: 'boundsUserAnnotations', label: 'user-annotations', icon: 'fa-draw-polygon', max: this.maxNbUserAnnotations},
        {key: 'reviewedAnnotations', boundsProp: 'boundsReviewedAnnotations', label: 'reviewed-annotations', icon: 'fa-check-double', max: this.maxNbReviewedAnnotations},
      ];
    },

    // active-filter chips, derived from the APPLIED (Vuex-synced) state. A multiselect is
    // active only as a strict subset (empty or all = "no filter"); a range is active when it
    // narrows [0, max]. Clearing a chip resets that filter (multiselects → all → stored null).
    chips() {
      const out = [];
      const onto = this.selectedOntologies;
      if (onto.length > 0 && onto.length < this.availableOntologies.length) {
        out.push({key: 'ontology', label: this.$t('ontology') + ': ' + onto.map(o => o.name).join(', ')});
      }
      const roles = this.selectedRoles;
      if (roles.length > 0 && roles.length < this.availableRoles.length) {
        out.push({key: 'role', label: this.$t('my-role') + ': ' + roles.join(', ')});
      }
      const tags = this.selectedTags;
      if (tags.length > 0 && tags.length < this.availableTags.length) {
        out.push({key: 'tag', label: this.$t('tags') + ': ' + tags.map(t => t.name).join(', ')});
      }
      for (const r of this.rangeDefs) {
        const bounds = this[r.boundsProp];
        if (bounds[0] > 0 || bounds[1] < r.max) {
          out.push({key: r.key, label: this.$t(r.label) + ': ' + bounds[0] + '–' + bounds[1]});
        }
      }
      return out;
    },

    projectCollection() {
      let collection = new ProjectCollection({
        withMembersCount: true,
        withLastActivity: true,
        withCurrentUserRoles: true
      });
      if (this.selectedOntologiesIds.length > 0 && this.selectedOntologiesIds.length < this.availableOntologies.length) {
        collection['ontology'] = {
          in: this.selectedOntologiesIds.join()
        };
      }
      if (this.selectedRoles.length > 0) {
        collection['currentUserRole'] = {
          in: this.selectedRoles.join().toLowerCase()
        };
      }
      if (this.searchString) {
        collection['name'] = {
          ilike: encodeURIComponent(this.searchString)
        };
      }
      if (this.selectedTags.length > 0 && this.selectedTags.length < this.availableTags.length) {
        collection['tag'] = {
          in: this.selectedTags.map(t => t.id).join()
        };
      }
      for (let {prop, bounds, max} of this.boundsFilters) {
        collection[prop] = {};
        if (bounds[1] !== max) {
          // if max bounds is the max possible value, do not set the filter in the request
          // so that if an event (ex: algo creates an annotation) happens between the bounds request and the query request
          // the image will not be skipped from the result
          collection[prop] = {
            lte: bounds[1]
          };
        }
        if (bounds[0] > 0) {
          collection[prop]['gte'] = bounds[0];
        }
      }
      return collection;
    },
    currentPage: sync('listProjects/currentPage'),
    perPage: sync('listProjects/perPage'),
    sortField: sync('listProjects/sortField'),
    sortOrder: sync('listProjects/sortOrder')
  },
  watch: {
    // UI-7 C4: the grid fetches its own page (no ims-table). Any filter/search
    // change recomputes projectCollection → reset to page 1 and refetch.
    projectCollection() {
      if (this.currentPage !== 1) {
        this.currentPage = 1; // the currentPage watcher refetches
      } else {
        this.fetchProjects();
      }
    },
    currentPage() {
      this.fetchProjects();
    },
    perPage() {
      if (this.currentPage !== 1) {
        this.currentPage = 1;
      } else {
        this.fetchProjects();
      }
    },
    revision() {
      this.fetchOntologies();
      this.fetchMaxFilters();
      this.fetchProjects();
    },
    querySearchTags(values) {
      if (values) {
        this.selectedTags = [];
        let queriedTags = this.availableTags.filter(tag => values.split(',').includes(tag.name));
        if (queriedTags) {
          this.selectedTags = queriedTags;
        }
      }
    }
  },
  methods: {
    async fetchOntologies() {
      let ontologies = (await OntologyCollection.fetchAll({light: true})).array;
      ontologies.sort((a, b) => a.name.localeCompare(b.name));
      this.ontologies = ontologies;
    },
    async fetchMaxFilters() {
      let stats = await ProjectCollection.fetchBounds({withMembersCount:true});

      this.maxNbMembers = Math.max(10, stats.members.max);
      this.maxNbImages = Math.max(10, stats.numberOfImages.max);
      this.maxNbUserAnnotations = Math.max(100, stats.numberOfAnnotations.max);
      this.maxNbReviewedAnnotations = Math.max(100, stats.numberOfReviewedAnnotations.max);
    },
    async fetchTags() {
      this.availableTags = [{id: 'null', name: this.$t('no-tag')}, ...(await TagCollection.fetchAll()).array];
    },
    toggleFilters() {
      if (this.filtersOpen) {
        this.closeFilters();
      } else {
        this.buildDraft();
        this.filtersOpen = true;
      }
    },
    closeFilters() {
      this.filtersOpen = false;
    },
    // snapshot the applied filters into an editable draft (getters return "all" when unset).
    buildDraft() {
      this.draft = {
        ontologies: this.selectedOntologies.slice(),
        roles: this.selectedRoles.slice(),
        tags: this.selectedTags.slice(),
        members: this.boundsMembers.slice(),
        images: this.boundsImages.slice(),
        userAnnotations: this.boundsUserAnnotations.slice(),
        reviewedAnnotations: this.boundsReviewedAnnotations.slice(),
      };
    },
    resetDraft() {
      this.draft = {
        ontologies: this.availableOntologies.slice(),
        roles: this.availableRoles.slice(),
        tags: this.availableTags.slice(),
        members: [0, this.maxNbMembers],
        images: [0, this.maxNbImages],
        userAnnotations: [0, this.maxNbUserAnnotations],
        reviewedAnnotations: [0, this.maxNbReviewedAnnotations],
      };
    },
    setDraftBound(key, idx, raw) {
      const max = this.rangeDefs.find(r => r.key === key).max;
      const arr = this.draft[key].slice();
      let n = (raw === '' || raw === null) ? (idx === 0 ? 0 : max) : Math.round(Number(raw));
      if (Number.isNaN(n)) {
        n = idx === 0 ? 0 : max;
      }
      n = Math.max(0, Math.min(max, n));
      arr[idx] = n;
      // keep low ≤ high
      if (idx === 0 && arr[0] > arr[1]) {
        arr[1] = arr[0];
      }
      if (idx === 1 && arr[1] < arr[0]) {
        arr[0] = arr[1];
      }
      this.$set(this.draft, key, arr);
    },
    applyFilters() {
      this.selectedOntologies = this.draft.ontologies;
      this.selectedRoles = this.draft.roles;
      this.selectedTags = this.draft.tags;
      this.boundsMembers = this.draft.members;
      this.boundsImages = this.draft.images;
      this.boundsUserAnnotations = this.draft.userAnnotations;
      this.boundsReviewedAnnotations = this.draft.reviewedAnnotations;
      this.filtersOpen = false;
    },
    clearAll() {
      this.selectedOntologies = this.availableOntologies.slice();
      this.selectedRoles = this.availableRoles.slice();
      this.selectedTags = this.availableTags.slice();
      this.boundsMembers = [0, this.maxNbMembers];
      this.boundsImages = [0, this.maxNbImages];
      this.boundsUserAnnotations = [0, this.maxNbUserAnnotations];
      this.boundsReviewedAnnotations = [0, this.maxNbReviewedAnnotations];
    },
    // remove a single active-filter chip (resets that filter to its "all"/full-range default).
    clearChip(key) {
      switch (key) {
        case 'ontology':
          this.selectedOntologies = this.availableOntologies.slice();
          break;
        case 'role':
          this.selectedRoles = this.availableRoles.slice();
          break;
        case 'tag':
          this.selectedTags = this.availableTags.slice();
          break;
        case 'members':
          this.boundsMembers = [0, this.maxNbMembers];
          break;
        case 'images':
          this.boundsImages = [0, this.maxNbImages];
          break;
        case 'userAnnotations':
          this.boundsUserAnnotations = [0, this.maxNbUserAnnotations];
          break;
        case 'reviewedAnnotations':
          this.boundsReviewedAnnotations = [0, this.maxNbReviewedAnnotations];
          break;
      }
    },
    async fetchProjects() {
      this.loadingPage = true;
      try {
        let collection = this.projectCollection.clone();
        collection.max = this.perPage;
        collection.sort = this.sortField || 'lastActivity';
        collection.order = this.sortOrder || 'desc';
        let data = await collection.fetchPage(this.currentPage - 1);
        this.projects = data.array;
        this.total = data.totalNbItems;
      } catch (error) {
        console.log(error);
        this.projects = [];
        this.total = 0;
      }
      this.loadingPage = false;
    },
    openDetail(project) {
      this.detailProject = project;
      this.detailModalActive = true;
    },
    async onModalDelete() {
      this.detailModalActive = false;
      if (this.detailProject) {
        await this.deleteProject(this.detailProject);
      }
    },
    updateProject() {
      this.revision++;
    },
    async deleteProject(projectToDelete) {
      try {
        await projectToDelete.delete();
        this.revision++;
        this.$notify({
          type: 'success',
          text: this.$t('notif-success-project-deletion', {projectName: projectToDelete.name})
        });
      } catch (error) {
        this.$notify({
          type: 'error',
          text: this.$t('notif-error-project-deletion', {projectName: projectToDelete.name})
        });
        return;
      }
    }
  },
  async created() {
    try {
      await Promise.all([
        this.fetchOntologies(),
        this.fetchMaxFilters(),
        this.fetchTags()
      ]);
    } catch (error) {
      console.log(error);
      this.error = true;
    }
    if (this.$route.query.tags) {
      let queriedTags = this.availableTags.filter(tag => this.$route.query.tags.split(',').includes(tag.name));
      if (queriedTags) {
        this.selectedTags = queriedTags;
      }
    }

    await this.fetchProjects();
    this.loading = false;
  }
};
</script>

<style scoped lang="scss">
@import '@/assets/styles/colors.scss';

/* design tokens (Projects.html) mapped to our palette */
$blue: $primary;
$blue-dark: $dark-primary;
$ink: #22262e;
$muted: #6b7280;
$muted-2: #9aa1ab;
$line: #e4e5e9;
$line-soft: #eceef1;
$panel: #f5f6f8;
$chip: #eef3fc;

/* full-bleed white surface; kill the global .content-wrapper gutter so other pages
   are unaffected. fa icons here are laid out with gap, not the global right-pad. */
.list-projects-wrapper.content-wrapper { padding: 0; }
.proj-page {
  background: #fff;
  min-height: calc(100vh - 50px);
  padding: 22px 26px 60px;

  ::v-deep .fas { padding-right: 0; }
}

/* ---------------------------------------------------------------- toolbar */
.proj-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
}
.tb-search {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 320px;
  flex: none;
  height: 38px;
  padding: 0 13px;
  border: 1px solid $line;
  border-radius: 8px;
  background: #fff;
  color: $muted-2;

  .tb-ico { display: flex; font-size: 0.9rem; }
  input {
    border: 0;
    outline: 0;
    background: transparent;
    font-size: 14px;
    width: 100%;
    color: $ink;
  }
}
.tb-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

/* trigger button + popover */
.filter-anchor { position: relative; }
.trigger-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 14px;
  border: 1px solid $line;
  border-radius: 8px;
  background: #fff;
  color: $ink;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;

  &:hover { background: #f7f8fa; }
  &.is-open { border-color: $blue; box-shadow: 0 0 0 3px rgba($blue, 0.12); }
  &.has-active { border-color: $blue; color: $blue; }
}
.count {
  display: grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: $blue;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}
.fb-chev { color: $muted-2; font-size: 0.7rem; transition: transform 0.15s; }
.fb-chev.up { transform: rotate(180deg); }

/* buttons */
.primary-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  height: 38px;
  padding: 0 16px;
  border: 0;
  border-radius: 8px;
  background: $blue;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  &:hover { background: $blue-dark; }
}
.ghost-btn {
  height: 38px;
  padding: 0 16px;
  border: 1px solid $line;
  border-radius: 8px;
  background: #fff;
  color: $ink;
  font-size: 14px;
  font-weight: 500;
  &:hover { background: $panel; }
}
.link-btn {
  border: 0;
  background: transparent;
  color: $blue;
  font-size: 13px;
  font-weight: 600;
  &:hover { text-decoration: underline; }
}

.popover2 {
  position: absolute;
  top: 46px;
  right: 0;
  width: 580px;
  max-width: 92vw;
  background: #fff;
  border: 1px solid $line;
  border-radius: 14px;
  box-shadow: 0 18px 50px rgba(20, 28, 45, 0.18);
  padding: 20px;
  z-index: 50;
  animation: pop 0.14s ease-out;
}
@keyframes pop { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
.p2-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 16px;

  span { display: flex; align-items: center; gap: 8px; }
  span .fas { color: $blue; }
}
.p2-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
}
.p2-divider { height: 1px; background: $line-soft; margin: 18px 0; }
.p2-ranges {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 22px;
}
.p2-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid $line-soft;
}

/* field label */
.field { display: flex; flex-direction: column; gap: 7px; min-width: 0; }
.field-lbl {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: $muted;

  .fas { color: $muted-2; font-size: 0.82em; }
}

/* min/max number inputs */
.minmax { display: flex; align-items: center; gap: 9px; }
.mm-box {
  flex: 1;
  width: 100%;
  min-width: 0;
  height: 36px;
  padding: 0 10px;
  border: 1px solid $line;
  border-radius: 7px;
  background: #fff;
  font-size: 14px;
  font-weight: 500;
  color: $ink;
  text-align: center;
  outline: none;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  &:hover { border-color: #cdd2da; }
  &:focus { border-color: $blue; box-shadow: 0 0 0 3px rgba($blue, 0.12); }
}
.mm-dash { color: $muted-2; font-size: 12px; font-weight: 600; flex: none; }

/* compact multiselect inside the popover */
.field ::v-deep .multiselect { font-size: 13px; min-height: 36px; }
.field ::v-deep .multiselect__tags {
  min-height: 36px;
  padding: 5px 36px 0 8px;
  border-radius: 7px;
  border-color: $line;
}
.field ::v-deep .multiselect__select { height: 34px; }

/* active-filter chips */
.chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: -2px 0 16px;
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 30px;
  padding: 0 6px 0 12px;
  border-radius: 16px;
  background: $chip;
  color: $blue;
  font-size: 13px;
  font-weight: 500;

  button {
    display: grid;
    place-items: center;
    width: 18px;
    height: 18px;
    border: 0;
    border-radius: 50%;
    background: rgba($blue, 0.15);
    color: $blue;
    font-size: 0.6rem;
    &:hover { background: rgba($blue, 0.28); }
  }
}
.chip-clear {
  height: 30px;
  border: 0;
  background: transparent;
  color: $muted;
  font-size: 13px;
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 2px;
  &:hover { color: $ink; }
}

/* card grid */
.proj-area { position: relative; min-height: 12rem; }
.proj-grid { margin-bottom: 0.5rem; }
.no-proj { padding: 3rem 0; }

@media screen and (max-width: 768px) {
  .proj-toolbar { flex-wrap: wrap; }
  .tb-search { width: 100%; }
  .tb-actions { margin-left: 0; }
  .popover2 { width: 92vw; }
  .p2-row, .p2-ranges { grid-template-columns: 1fr; }
}
</style>

<style lang="scss">
@import '@/assets/styles/colors.scss';

.search-projects {
  max-width: 25em;
  margin-right: 1em;
}

.table-projects {
  margin-top: 1rem;
}

/* UI-3: clean ZayaAI table (design: .ptbl) — hairline rows, blue links, row hover. */
.list-projects-wrapper {
  .table {
    background: transparent;

    thead th {
      border-bottom: 2px solid $line-1;
      color: #3c414a;
      font-weight: 700;
      vertical-align: middle !important;
    }

    td {
      border-bottom: 1px solid $line-1;
      color: $ink-3;
      vertical-align: middle !important;
    }

    tbody tr:hover {
      background: #fafbfc;
    }

    /* Blue text links — but NOT the .button anchors (Open), whose text must stay white. */
    a:not(.button) {
      color: $primary;
      font-weight: 600;
    }
  }

  /* Blue "Open" action, compact (design: .open-btn) — white label at rest and on hover. */
  .button.is-link {
    background: $primary;
    border-color: $primary;
    color: #fff;

    &:hover,
    &:focus {
      background: $dark-primary;
      border-color: $dark-primary;
      color: #fff;
    }
  }
}
</style>
